import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { PanelHead, Badge, Empty, Btn, useFocusRecord, focusCls } from "./common";
import { Upload, Trash2, Image as ImageIcon, Mic, Camera } from "lucide-react";
import { toast } from "sonner";

export const FilesPanel = ({ onChanged, siteMode = false, focusId }) => {
  const { t, tx } = useI18n();
  const [items, setItems] = useState([]);
  useFocusRecord(items, focusId);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState("");
  const [caption, setCaption] = useState("");
  const [preview, setPreview] = useState(null);

  const load = async () => {
    const [f, p] = await Promise.all([api.get("/files"), api.get("/projects")]);
    setItems(f.data); setProjects(p.data);
  };
  useEffect(() => { load(); }, []);

  const upload = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      await api.post("/files", { filename: file.name, content_type: file.type, data: reader.result, project_id: project, caption, kind: "photo" });
      toast.success(tx("{name} yüklendi", { name: file.name })); setCaption(""); load(); onChanged?.();
    };
    reader.readAsDataURL(file);
  };
  const del = async (id) => { await api.delete(`/files/${id}`); load(); onChanged?.(); };
  const view = async (id) => { const r = await api.get(`/files/${id}/data`); setPreview(r.data.data); };

  return (
    <div data-testid={siteMode ? "panel-site-mode" : "panel-files"}>
      <PanelHead title={siteMode ? t("nav_site") : t("nav_files")} sub={siteMode ? tx("Tek ekrandan hızlı fotoğraf + not") : tx("Fotoğraf & belge arşivi")} />
      <div className={`panel p-5 mb-5 ${siteMode ? "text-lg" : ""}`}>
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <label className="block">
            <span className="label-eyebrow block mb-1">{tx("Projeye bağla")}</span>
            <select value={project} onChange={(e) => setProject(e.target.value)} className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white">
              <option value="" className="bg-[#0C1222]">—</option>
              {projects.map((p) => <option key={p.project_id} value={p.project_id} className="bg-[#0C1222]">{p.name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="label-eyebrow block mb-1">{tx("Açıklama / iş notu")}</span>
            <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder={tx("Örn: Bugün boruyu değiştirdik")}
              className="w-full bg-black/30 border border-cyan-500/20 rounded-lg px-3 py-2 text-sm text-white" data-testid="file-caption-input" />
          </label>
        </div>
        <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-cyan-500/30 rounded-xl cursor-pointer hover:border-cyan-400/60 text-cyan-300 ${siteMode ? "py-10 text-xl" : "py-6"}`} data-testid="file-upload-input">
          {siteMode ? <Camera size={28} /> : <Upload size={20} />} {tx("Fotoğraf / belge yükle")}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={upload} capture={siteMode ? "environment" : undefined} />
        </label>
        {siteMode && <p className="text-xs text-amber-300/80 mt-3">{tx("⚠️ İnternet kesilirse yüklemeler gönderilemez; bağlantı gelince tekrar deneyin. Gürültülü ortamda net olmayan ölçü/adres/tutarı uydurmam — size sorarım.")}</p>}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {items.length === 0 && <Empty text={t("noData")} />}
        {items.map((x) => (
          <div key={x.file_id} data-rid={x.file_id} className={`panel p-3${focusCls(x.file_id, focusId)}`} data-testid="file-row">
            <button onClick={() => view(x.file_id)} className="w-full aspect-square rounded-lg bg-black/40 flex items-center justify-center text-cyan-400/60 mb-2 overflow-hidden">
              <ImageIcon size={28} />
            </button>
            <div className="text-xs text-white truncate">{x.filename}</div>
            {x.caption && <div className="text-[0.68rem] text-[color:var(--txt-2)] truncate">{x.caption}</div>}
            <button onClick={() => del(x.file_id)} className="text-white/40 hover:text-red-400 mt-1"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      {preview && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setPreview(null)} data-testid="file-preview-modal">
          <img src={preview} alt="" className="max-h-[85vh] max-w-full rounded-xl border border-cyan-500/30" />
        </div>
      )}
    </div>
  );
};

export default FilesPanel;
